import { useEffect } from "react";

const SITE_URL =
    typeof window !== "undefined" ? window.location.origin : "https://pronospot.com";

// Lightweight SEO helper: sets <title>, meta description, canonical, and injects JSON-LD.
// Cleans up its injected JSON-LD on unmount so different pages don't accumulate scripts.
export const SEO = ({
    title,
    description,
    keywords,
    canonical,
    image,
    type = "website",
    robots,
    jsonLd,
    prev,
    next,
}) => {
    useEffect(() => {
        if (title) document.title = title;

        const setMeta = (name, content) => {
            if (!content) return null;
            let el = document.querySelector(`meta[name="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("name", name);
                document.head.appendChild(el);
            }
            const prev = el.getAttribute("content");
            el.setAttribute("content", content);
            return () => {
                if (prev !== null) el.setAttribute("content", prev);
                else el.remove();
            };
        };

        const setProperty = (property, content) => {
            if (!content) return null;
            let el = document.querySelector(`meta[property="${property}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("property", property);
                document.head.appendChild(el);
            }
            const prev = el.getAttribute("content");
            el.setAttribute("content", content);
            return () => {
                if (prev !== null) el.setAttribute("content", prev);
                else el.remove();
            };
        };

        const setLink = (rel, href) => {
            if (!href) return null;
            let el = document.querySelector(`link[rel="${rel}"]`);
            if (!el) {
                el = document.createElement("link");
                el.setAttribute("rel", rel);
                document.head.appendChild(el);
            }
            const prev = el.getAttribute("href");
            el.setAttribute("href", href);
            return () => {
                if (prev !== null) el.setAttribute("href", prev);
                else el.remove();
            };
        };

        const ogImage = image
            ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
            : `${SITE_URL}/logo192.png`;

        const cleanPath = typeof window !== "undefined"
            ? (window.location.pathname === "/index.html" ? "/" : window.location.pathname)
            : "/";
        const canonicalUrl = canonical || `${SITE_URL}${cleanPath}`;

        const cleanups = [
            setMeta("description", description),
            setMeta("keywords", keywords || "bookmaker, bonus paris sportifs, cotes football, paris football, ANJ, Winamax, Betclic, Unibet, comparatif bookmakers"),
            setMeta("robots", robots || "index, follow"),
            setLink("canonical", canonicalUrl),
            setLink("prev", prev),
            setLink("next", next),
            // Open Graph
            setProperty("og:title", title),
            setProperty("og:description", description),
            setProperty("og:url", canonicalUrl),
            setProperty("og:type", type),
            setProperty("og:image", ogImage),
            // Twitter Cards
            setMeta("twitter:card", "summary_large_image"),
            setMeta("twitter:title", title),
            setMeta("twitter:description", description),
            setMeta("twitter:image", ogImage),
        ].filter(Boolean);

        const scripts = [];
        if (jsonLd) {
            const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            items.forEach((obj) => {
                const s = document.createElement("script");
                s.type = "application/ld+json";
                s.setAttribute("data-pronospot-seo", "1");
                s.text = JSON.stringify(obj);
                document.head.appendChild(s);
                scripts.push(s);
            });
        }

        return () => {
            cleanups.forEach((fn) => fn && fn());
            scripts.forEach((s) => s.remove());
        };
    }, [title, description, keywords, canonical, image, type, robots, jsonLd, prev, next]);

    return null;
};

export const orgSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Pronospot",
    url: SITE_URL,
    logo: `${SITE_URL}/logo192.png`,
    sameAs: [],
});

export const breadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: it.url,
    })),
});

export const reviewSchema = (bookmaker) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
        "@type": "Organization",
        name: bookmaker.name,
        url: SITE_URL + "/avis/" + bookmaker.slug,
    },
    reviewRating: {
        "@type": "Rating",
        ratingValue: bookmaker.score,
        bestRating: 10,
        worstRating: 0,
    },
    author: {
        "@type": "Organization",
        name: "Pronospot",
    },
    publisher: {
        "@type": "Organization",
        name: "Pronospot",
    },
    reviewBody: bookmaker.description,
});

export const articleSchema = ({
    title,
    description,
    author = "Pronospot",
    datePublished,
    url,
}) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    author: { "@type": "Person", name: author },
    publisher: {
        "@type": "Organization",
        name: "Pronospot",
        logo: {
            "@type": "ImageObject",
            url: `${SITE_URL}/logo192.png`,
        },
    },
    datePublished: datePublished || new Date().toISOString().slice(0, 10),
    mainEntityOfPage: url,
});

export const faqSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
        },
    })),
});

export const sportsEventSchema = (match) => ({
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: `${match.home} vs ${match.away}`,
    startDate: match.kickoff,
    sport: "https://en.wikipedia.org/wiki/Association_football",
    homeTeam: {
        "@type": "SportsTeam",
        name: match.home,
    },
    awayTeam: {
        "@type": "SportsTeam",
        name: match.away,
    },
    eventStatus: "https://schema.org/EventScheduled",
});

export default SEO;
